import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { DynamicFormQuestionComponent } from '../../../../../inputfileds/dynamic-form-question/dynamic-form-question.component';
import { QuestionControlService } from '../../../../../inputfileds/form_services/question-control.service';
import { ApiservicesService } from '../../../../../Services/Apis/apiservices.service';
import { FrontpageService } from '../../../../../inputfileds/form_services/collegemanagement/frontpage.service';
import { TranslateDirective } from '../../../../Utilites/Directives/translate.directive';
import { TranslatePipe } from '../../../../Utilites/Pipe/translate.pipe';
import { Inputfiledsbase } from '../../../../../inputfileds/models/inputfiledsbase';
import { Observable } from 'rxjs';

interface DialogData {
  content_type?: 'missions' | 'visions';
  rowData?: any;
  websiteId?: string;
  pageId?: string;
  createApiEndpoint?: string;
  updateApiEndpoint?: string;
}

@Component({
  selector: 'app-addmissionvision',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatDividerModule,
    DynamicFormQuestionComponent,
    MatIconModule,
    TranslateDirective,
    TranslatePipe,
  ],
  templateUrl: './addmissionvision.component.html',
  styleUrl: './addmissionvision.component.scss',
  providers: [QuestionControlService, ApiservicesService],
})
export class AddmissionvisionComponent implements OnInit, OnDestroy {
  questions: Inputfiledsbase<string>[] = [];
  questions$: Observable<Inputfiledsbase<any>[]> | null = null;
  missionVissionForm!: FormGroup;
  isEditMode: boolean = false;

  // Image handling
  selectedImageUrl: string | null = null;
  selectedFile: File | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<AddmissionvisionComponent>,
    private frontpageService: FrontpageService,
    private apiservice: ApiservicesService,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.questions$ = this.frontpageService.getMissionVissionForm();
  }

  ngOnInit(): void {
    // Load questions
    this.questions$?.subscribe((q) => {
      this.questions = q ?? [];
      this.cdr.detectChanges();
    });

    this.isEditMode = !!this.data?.rowData;

    // Initialize form
    this.missionVissionForm = this.fb.group({
      content_type: [this.data?.content_type || '', Validators.required],
      description: [
        this.data?.rowData?.content ||
          this.data?.rowData?.info ||
          this.data?.rowData?.description ||
          '',
        Validators.required,
      ],
    });

    // Load existing image in edit mode
    if (this.isEditMode && this.data?.rowData?.image_url) {
      const baseUrl = this.apiservice.environmentPath || '';
      // Ensure no double slash
      this.selectedImageUrl = `${baseUrl.replace(/\/$/, '')}/${this.data.rowData.image_url.replace(/^\//, '')}`;
    }
  }

  ngOnDestroy(): void {
    // Clean up blob URLs to prevent memory leaks
    if (this.selectedImageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.selectedImageUrl);
    }
  }

  imageInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file || !file.type.startsWith('image/')) {
      return;
    }

    this.selectedFile = file;

    // Create preview
    const previewUrl = URL.createObjectURL(file);

    // Clean up previous blob if exists
    if (this.selectedImageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.selectedImageUrl);
    }

    this.selectedImageUrl = previewUrl;
    this.cdr.detectChanges();
  }

  onSubmit(): void {
    if (this.missionVissionForm.invalid) {
      this.missionVissionForm.markAllAsTouched();
      return;
    }

    const formData = new FormData();

    formData.append(
      'content',
      this.missionVissionForm.value.description.trim(),
    );
    formData.append('content_type', this.missionVissionForm.value.content_type);
    formData.append('website_id', this.data.websiteId || '');
    formData.append('website_page_id', this.data.pageId || '');

    // Attach image only if a new file was selected
    if (this.selectedFile) {
      formData.append('image_url', this.selectedFile); // ← confirm this field name with backend
      // Alternative common names: 'image', 'file', 'upload', 'banner_image'
    }

    if (this.isEditMode) {
      this.updateMissionVission(formData);
    } else {
      this.createMissionVissionData(formData);
    }
  }

  private createMissionVissionData(formData: FormData): void {
    const endpoint = this.data.createApiEndpoint;
    if (!endpoint) {
      console.error('Create endpoint missing');
      return;
    }

    this.apiservice.createMissionVission(endpoint, formData).subscribe({
      next: (res: any) => {
        if (res.code === 200 && res.status === true) {
          this.missionVissionForm.reset();
          this.closeDialog(true);
        } else {
          console.error('Create failed:', res.message);
        }
      },
      error: (err) => {
        console.error('Create error:', err);
      },
    });
  }

  private updateMissionVission(formData: FormData): void {
    const endpoint = this.data.updateApiEndpoint;
    const id = this.data.rowData?.id;

    if (!endpoint || !id) {
      console.error('Update endpoint or ID missing');
      return;
    }

    this.apiservice.updateMissionVission(endpoint, id, formData).subscribe({
      next: (res: any) => {
        if (res.code === 200 && res.status === true) {
          this.missionVissionForm.reset();
          this.closeDialog(true);
        } else {
          console.error('Update failed:', res.message);
        }
      },
      error: (err) => {
        console.error('Update error:', err);
      },
    });
  }

  closeDialog(success: boolean = false): void {
    // Optional animation cleanup if you still use animate.css
    const element = document.querySelector('.animate__animated');
    if (element) {
      element.classList.remove('animate__fadeIn');
      element.classList.add('animate__fadeOut');
    }

    this.dialogRef.close(success);
  }
}
